import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, MapPin, Clock, Eye, DollarSign, Shield, Zap, Package, ArrowRight } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const HERO_IMG = 'https://images.unsplash.com/photo-1759272548470-d0686d071036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBzaGlwcGluZyUyMGxvZ2lzdGljcyUyMGNhcmdvfGVufDB8fHx8MTc3MzI2Nzc5Mnww&ixlib=rb-4.1.0&q=85&w=1600';

const steps = [
  { num: '01', title: 'Enter addresses', desc: 'Full Canadian address support. Search and auto-complete finds your locations instantly.' },
  { num: '02', title: 'Describe your cargo', desc: 'Tell us what you are shipping, the weight, and any special requirements.' },
  { num: '03', title: 'Get instant pricing', desc: 'Our system calculates the route and gives you a fair, transparent price per mile.' },
  { num: '04', title: 'Track in real-time', desc: 'Watch your shipment move on a live map. Know exactly when it will arrive.' },
];

const features = [
  { icon: Eye, title: 'Real-Time Tracking', desc: 'Watch your freight move on a live map. See the truck, the route, and the ETA at a glance.' },
  { icon: DollarSign, title: 'Transparent Pricing', desc: 'No hidden fees. Price calculated automatically based on distance. What you see is what you pay.' },
  { icon: Clock, title: 'Live ETA', desc: 'Click any delivery point on the map to see the estimated time of arrival, updated in real-time.' },
  { icon: Shield, title: 'Status at Every Step', desc: 'From pickup to delivery, every status change is logged and visible. Full chain of custody.' },
  { icon: Zap, title: 'Book in Minutes', desc: 'Our Typeform-style wizard makes booking a shipment as easy as filling out a survey.' },
  { icon: Package, title: 'Full Load Support', desc: 'From 5,000 to 45,000 lbs. Palletized goods, equipment, raw materials - we move it all.' },
];

const trustPoints = [
  { value: '< 5 min', label: 'Average booking time' },
  { value: '$2.50/mi', label: 'Fair transparent rate' },
  { value: '100%', label: 'GPS-tracked loads' },
];

export default function ShippersLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="shippers-landing">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-zinc-100" data-testid="shippers-nav">
        <div className="flex items-center gap-3">
          <BrandLogo size="default" />
          <span className="text-xs text-zinc-400 border-l border-zinc-200 pl-3 hidden sm:block">For Shippers</span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-zinc-500 hover:text-zinc-900" onClick={() => navigate('/login')} data-testid="shipper-landing-login">
            Log In
          </Button>
          <Button className="bg-[#E5A100] hover:bg-[#E5A100]/100 text-white font-semibold rounded-full px-6"
            onClick={() => navigate('/register')} data-testid="shipper-landing-register">
            Ship Now <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-16 md:pt-24 pb-24 overflow-hidden" data-testid="shipper-hero">
        <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
          <img src={HERO_IMG} alt="Shipping port" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-[#E5A100]/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Package className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-xs font-medium tracking-wide">FOR SHIPPERS & CLIENTS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 text-zinc-900">
            Ship with confidence.<br />
            <span className="bg-gradient-to-r from-[#E5A100] via-[#E5A100] to-[#E5A100] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Track with clarity.
            </span>
          </h1>
          <p className="text-zinc-500 text-lg max-w-xl mb-10 leading-relaxed">
            Book a shipment in minutes, not hours. Get instant transparent pricing
            and watch your freight move in real-time on a live map.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button className="bg-[#E5A100] hover:bg-[#E5A100]/100 text-white font-semibold rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/register')} data-testid="shipper-hero-cta">
              Start Shipping <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" className="border-zinc-300 text-zinc-600 hover:bg-zinc-50 rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')}>
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="bg-zinc-50 border-y border-zinc-200 px-6 md:px-12 py-8">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8">
          {trustPoints.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-white">{s.value}</p>
              <p className="text-zinc-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 md:px-12 py-20" data-testid="shipper-how-it-works">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-base md:text-lg font-bold text-center mb-12">
            Book a shipment in <span className="text-white">4 simple steps</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-4 p-5 border border-zinc-200 rounded-2xl hover:border-white/30 hover:shadow-sm transition-all group">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-[#E5A100]/10 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{s.num}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-zinc-900 mb-1">{s.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-zinc-50 px-6 md:px-12 py-20" data-testid="shipper-features">
        <h2 className="text-base md:text-lg font-bold text-center mb-12">
          Why shippers <span className="text-white">love</span> TORQ
        </h2>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-[#E5A100]/10 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-2">{f.title}</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-20">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-[#E5A100] to-[#E5A100] rounded-3xl p-10 md:p-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to ship smarter?</h2>
          <p className="text-white200 mb-8 max-w-md mx-auto">
            Create your free account and book your first shipment in under 5 minutes. No credit card required.
          </p>
          <Button className="bg-white hover:bg-zinc-100 text-white font-semibold rounded-full px-8 py-6 text-lg"
            onClick={() => navigate('/register')} data-testid="shipper-bottom-cta">
            Create Shipper Account <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 px-6 md:px-12 py-6 flex items-center justify-between">
        <BrandLogo size="sm" className="opacity-50" />
        <div className="flex items-center gap-6 text-xs text-zinc-400">
          <button onClick={() => navigate('/')} className="hover:text-zinc-600 transition-colors">Home</button>
          <button onClick={() => navigate('/for-drivers')} className="hover:text-zinc-600 transition-colors">For Drivers</button>
          <button onClick={() => navigate('/for-fleets')} className="hover:text-zinc-600 transition-colors">For Fleets</button>
        </div>
      </footer>
    </div>
  );
}
